// ─── AUTH INIT — Supabase client + auth event handlers ─────────────────────
//
// Crea il Supabase client + espone `window.sb`, registra il listener
// `onAuthStateChange` (login/logout/password recovery), e il listener
// `visibilitychange` (token refresh al ritorno foreground).
//
// Estratto da `index.html` <script> classic inline in PR refactor/bkl016-pr3
// (2026-05-21, completamento ADR-0005 split). Chiude **BKL-086** (refactor
// auth init per defer supabase / preparazione per cleanup window.* bridge
// BKL-067).
//
// ━━━ Race condition INITIAL_SESSION — gestione ━━━
//
// Storia: la versione inline nello <script> classic veniva caricata sync al
// parse time, prima che `INITIAL_SESSION` potesse essere emesso. La PR #287
// (BKL-058) ha documentato che spostare in modulo ESM `<script type="module">`
// (deferred) potrebbe creare race: client creato dopo parsing, INITIAL_SESSION
// emesso prima del listener register.
//
// Soluzione (audit 2026-05-21): Supabase JS emette `INITIAL_SESSION` DOPO il
// primo `createClient()` call, NON al parse del CDN script. Quindi se
// chiamiamo createClient + onAuthStateChange entrambi sync nello stesso modulo
// (questa file), il listener è registrato PRIMA dell'emissione → safe.
//
// Differenza pratica vs versione classic inline: login screen mostrato ~10-50ms
// più tardi (acceptable, no UX impact reale). Vantaggio: codice in modulo ESM
// con import.meta.env nativo, no hack inline `__VITE_SUPABASE_URL__` token
// (rimosso da index.html in questa stessa PR).
//
// ━━━ Bridge window.* ━━━
//
// `window.sb` esposto per consumo da builders.js, generators.js, ui-*.
// `window.currentUser` settato dentro handler (era bare identifier in script
// classic non-strict; ora esplicito window.* per coerenza ESM).
// `window._pendingRecovery` esposto perché può essere letto dall'esterno
// (memo: nessun consumatore esterno trovato 2026-05-21, ma per coerenza).
// `_initialized` resta variabile interna al modulo (no consumer esterni).
//
// Cleanup window.* bridge eventuale → BKL-067 (refactor per usare ES imports).

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!SUPABASE_URL || !SUPABASE_KEY) {
  console.error('[auth-init] VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY missing — login will fail');
}

// `supabase` global caricato sync via CDN <script src="...supabase-js@2"> in
// index.html, disponibile prima che questo modulo (deferred) parta.
const sb = supabase.createClient(SUPABASE_URL, SUPABASE_KEY, {
  auth: { flowType: 'implicit' }
});
window.sb = sb;

// Recovery flag — true durante reset password (blocca login finché user
// non completa il reset).
window._pendingRecovery = false;

// Internal: evita doppio init handler post-login (es. SIGNED_IN + INITIAL_SESSION
// che fires entrambi al primo login post-OAuth).
let _initialized = false;

// ─── Gestione cambio stato auth (login, logout, redirect dal link di reset) ──
sb.auth.onAuthStateChange(async (event, session) => {
  if (event === 'PASSWORD_RECOVERY') {
    window._pendingRecovery = true;
    document.getElementById('resetPasswordModal').style.display = 'flex';
    return;
  }
  if (window._pendingRecovery) return; // blocca login finché la nuova password non è impostata
  if (session?.user) {
    if (_initialized) return; // evita doppio init
    _initialized = true;
    window.currentUser = session.user;
    document.getElementById('loginScreen').classList.add('hide');
    const app = document.getElementById('mainApp');
    app.style.display = 'flex';
    app.style.flexDirection = 'column';
    app.style.overflow = 'hidden';
    app.style.flex = '1';
    // Defer loadUserData fuori dall'handler: Supabase JS usa Web Locks API per
    // sincronizzare l'auth state, e una query REST chiamata DENTRO l'handler
    // di onAuthStateChange (post-F5/INITIAL_SESSION/SIGNED_IN dopo reload) può
    // bloccarsi indefinitamente in attesa dello stesso lock — deadlock.
    // setTimeout(0) lascia ritornare l'handler, rilascia il lock, poi parte il load.
    setTimeout(() => {
      if (typeof window.loadUserData === 'function') {
        window.loadUserData().catch(e => console.error('[load] failed:', e));
      } else {
        console.error('[auth-init] window.loadUserData not exposed — ui-onboarding bridge missing');
      }
    }, 0);
  } else {
    _initialized = false;
    window.currentUser = null;
    document.getElementById('loginScreen').classList.remove('hide');
    document.getElementById('mainApp').style.display = 'none';
    // BKL-045: clear msgs + reset hero on SIGNED_OUT (race condition fix)
    try { const m = document.getElementById('msgs'); if (m) m.innerHTML = ''; } catch(_) {}
    try {
      if (typeof window.updateChatHeroVisibility === 'function') {
        window.updateChatHeroVisibility();
      }
    } catch(_) {}
  }
});

// ─── Visibility change — refresh token sul ritorno in foreground ───────────
// Al ritorno in foreground: se il token sta scadendo, tenta un refresh
// breve. Se il refresh fallisce/timeout → forza logout pulito (utente
// atterra sulla login screen, no home appesa).
//
// Razionale: dopo background lungo i timer JS sono throttlati, l'auto-refresh
// interno di Supabase non scatta in tempo. Il requisito non è mantenere viva
// la sessione a tutti i costi — è non lasciare l'utente in uno stato ambiguo.
document.addEventListener('visibilitychange', async () => {
  if (document.visibilityState !== 'visible') {
    sb.auth.stopAutoRefresh();
    return;
  }
  sb.auth.startAutoRefresh();
  try {
    const { data: { session } } = await sb.auth.getSession();
    if (!session) return;
    const expMs = session.expires_at * 1000;
    if (expMs >= Date.now() + 60_000) return; // token ancora valido per >60s
    const result = await Promise.race([
      sb.auth.refreshSession().then(r => r.error ? {type:'error', msg: r.error.message} : {type:'ok'}),
      new Promise(r => setTimeout(() => r({type:'timeout'}), 5000))
    ]);
    if (result.type !== 'ok') {
      // Refresh fallito o troppo lento → forza login per non lasciare la home appesa
      if (typeof window._forceLogoutToLogin === 'function') {
        window._forceLogoutToLogin('refresh ' + result.type);
      } else {
        console.error('[auth-init] window._forceLogoutToLogin not exposed — ui-auth bridge missing');
      }
    }
  } catch (e) {
    console.warn('[auth] visibility refresh check failed:', e?.message);
    if (typeof window._forceLogoutToLogin === 'function') {
      window._forceLogoutToLogin('exception: ' + e?.message);
    }
  }
});
